import React, { useState, useEffect, useRef, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { Helmet } from 'react-helmet-async';
import { Star } from 'lucide-react';
import './App.css';
import LaunchScreen from './components/LaunchScreen';
import QuizWelcomeModal from './components/QuizWelcomeModal';
import AppStoreQrHover from './components/AppStoreQrHover';
import aNewHeart from './assets/images/a new heart.webp';
import RecoverGraph from './assets/images/Recover Graph.webp';
import UnclingEffectiveness from './assets/images/Uncling Effectiveness.webp';
import YouCanHeal from './assets/images/You Can Heal.webp';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollSectionBackground from './components/ScrollSectionBackground';
import ThreeMockups from './assets/images/Uncling v4 mockups.webp';
import AppStore from './assets/images/AppStore.png';
import GooglePlay from './assets/images/google play.png';
import JournalsImage from './assets/images/Journals.webp';
import LessonsImage from './assets/images/Lessons.webp';
import HeartsImage from './assets/images/Hearts.webp';
import AffirmationsImage from './assets/images/Affirmations.webp';
import NoContactTrackerImage from './assets/images/No contact Tracker.webp';
import DailyTasksImage from './assets/images/Daily Tasks.webp';
import ScreenTimeImage from './assets/images/Screen Time Shield.webp';
import CommunityImage from './assets/images/Community.webp';
import SarahMImage from './assets/images/Sarah M.webp';
import MichaelRImage from './assets/images/Michael R.webp';
import JenniferLImage from './assets/images/Jennifer L.webp';
import Orb1 from './assets/images/orbs/Circle 1.webp';
import Orb2 from './assets/images/orbs/Circle 2.webp';
import Orb3 from './assets/images/orbs/Circle 3.webp';
import Orb4 from './assets/images/orbs/Circle 4.webp';
import Orb5 from './assets/images/orbs/Circle 5.webp';
import Orb6 from './assets/images/orbs/Circle 6.webp';
import Orb7 from './assets/images/orbs/Circle 7.webp';
import Orb8 from './assets/images/orbs/Circle 8.webp';
import Orb9 from './assets/images/orbs/Circle 9.webp';
import Orb10 from './assets/images/orbs/Circle 10.webp';
import Orb11 from './assets/images/orbs/Circle 11.webp';
import Orb12 from './assets/images/orbs/Circle 12.webp';
import Orb13 from './assets/images/orbs/Circle 13.webp';
import Orb14 from './assets/images/orbs/Circle 14.webp';
import Orb15 from './assets/images/orbs/Circle 15.webp';
import Orb16 from './assets/images/orbs/Circle 16.webp';
import {
  appStoreUrl,
  googlePlayUrl,
  openAppStoreWithOpenAIAds,
  trackOpenAIAdsAppDownloadClick,
  trackOpenAIAdsPageView
} from './lib/openaiAds';
import {
  captureFbclid,
  trackAppStoreClick,
  trackMetaPageView
} from './lib/metaPixel';


// Route-level code splitting. Previously every route — including the quiz and a
// 94KB privacy policy — shipped in the homepage's single 517KB bundle.
const Careers = React.lazy(() => import('./components/Careers'));
const PrivacyPolicy = React.lazy(() => import('./components/PrivacyPolicy'));
const Security = React.lazy(() => import('./components/Security'));
const TermsConditions = React.lazy(() => import('./components/TermsConditions'));
const FAQs = React.lazy(() => import('./components/FAQs'));
const Contact = React.lazy(() => import('./components/Contact'));
const CancelSubscription = React.lazy(() => import('./components/CancelSubscription'));
const AboutUs = React.lazy(() => import('./components/AboutUs'));
const NoContactTracker = React.lazy(() => import('./components/NoContactTracker'));
const LimerenceApp = React.lazy(() => import('./components/LimerenceApp'));
const ExQuiz = React.lazy(() => import('./components/ExQuiz/ExQuiz'));
const AttachmentStyleQuiz = React.lazy(() => import('./components/AttachmentStyleQuiz'));
const AttachmentStyleResultPage = React.lazy(() => import('./components/AttachmentStyleResultPage'));
const BlogList = React.lazy(() => import('./components/Blog/BlogList'));
const BlogPost = React.lazy(() => import('./components/Blog/BlogPost'));

const ScrollRevealText = () => {
  const containerRef = React.useRef<HTMLParagraphElement>(null);
  const spansRef = React.useRef<(HTMLSpanElement | null)[]>([]);

  React.useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Start revealing when the element enters the bottom 80% of the screen
      const startPoint = windowHeight * 0.8;
      // Fully revealed when it reaches the top 30%
      const endPoint = windowHeight * 0.3;
      
      const elementTop = rect.top;
      
      // Calculate global progress: 0 at startPoint, 1 at endPoint
      let globalProgress = (startPoint - elementTop) / (startPoint - endPoint);
      globalProgress = Math.min(1, Math.max(0, globalProgress));
      
      // We have 3 parts to reveal sequentially
      const totalParts = 3;
      
      spansRef.current.forEach((span, index) => {
        if (!span) return;
        
        // Calculate progress for this specific part
        const partStart = index / totalParts;
        const partEnd = (index + 1) / totalParts;
        
        let partProgress = (globalProgress - partStart) / (partEnd - partStart);
        
        // Clamp progress between 0.1 (faded) and 1 (fully white)
        partProgress = Math.min(1, Math.max(0.1, partProgress));
        
        span.style.color = `rgba(255, 255, 255, ${partProgress})`;
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <p ref={containerRef} className="statistics-text">
      <span ref={el => spansRef.current[0] = el} style={{ transition: 'color 0.2s ease-out', color: 'rgba(255,255,255,0.1)' }}>
        <span className="highlight-stat">85% of people</span>
      </span>{' '}
      <span ref={el => spansRef.current[1] = el} style={{ transition: 'color 0.2s ease-out', color: 'rgba(255,255,255,0.1)' }}>
        break the no contact rule within the first two weeks.
      </span>{' '}
      <span ref={el => spansRef.current[2] = el} style={{ transition: 'color 0.2s ease-out', color: 'rgba(255,255,255,0.1)' }}>
        Uncling provides the system you need to stay strong.
      </span>
    </p>
  );
};

const AnimatedFeatureItem: React.FC<{ children: React.ReactNode; index: number }> = ({ children, index }) => {
  const [isVisible, setIsVisible] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const currentRef = itemRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '-50px'
      }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <div 
      ref={itemRef} 
      className={`feature-item ${index % 2 === 0 ? 'feature-item-image-left' : 'feature-item-image-right'} ${isVisible ? 'feature-item-visible' : 'feature-item-hidden'}`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {children}
    </div>
  );
};

const AnimatedRectangle: React.FC<{ children: React.ReactNode; className: string; delay?: number }> = ({ children, className }) => {
  const [scale, setScale] = useState(0.86);
  const rectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let frameId: number | null = null;
    const minScale = 0.82;
    const maxScale = 0.98;

    const updateScale = () => {
      frameId = null;

      if (!rectRef.current) return;

      const rect = rectRef.current.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;
      const rectCenter = rect.top + rect.height / 2;
      const distanceFromCenter = Math.abs(rectCenter - viewportCenter);
      const influenceRange = window.innerHeight * 0.85;
      const proximity = Math.max(0, 1 - distanceFromCenter / influenceRange);
      const nextScale = minScale + proximity * (maxScale - minScale);

      setScale(Number(nextScale.toFixed(3)));
    };

    const requestScaleUpdate = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(updateScale);
    };

    updateScale();
    window.addEventListener('scroll', requestScaleUpdate, { passive: true });
    window.addEventListener('resize', requestScaleUpdate);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      window.removeEventListener('scroll', requestScaleUpdate);
      window.removeEventListener('resize', requestScaleUpdate);
    };
  }, []);

  return (
    <div 
      ref={rectRef} 
      className={className}
      style={{ transform: `scale(${scale})` }}
    >
      {children}
    </div>
  );
};

type AttachmentStyleCard = {
  styleName: string;
  tone: 'secure' | 'anxious' | 'avoidant' | 'disorganized';
  meaning: string;
  quickIdentifiers: string[];
};

const attachmentStyleCards: AttachmentStyleCard[] = [
  {
    styleName: 'Secure',
    tone: 'secure',
    meaning: 'Comfortable with closeness and independence, even when conflict shows up.',
    quickIdentifiers: [
      'Communicates needs clearly without emotional games',
      'Can self-soothe and still stay emotionally available',
      'Repairs conflict directly instead of withdrawing or escalating'
    ]
  },
  {
    styleName: 'Anxious',
    tone: 'anxious',
    meaning: 'Craves reassurance and often fears rejection or abandonment.',
    quickIdentifiers: [
      'Reads delays in texts as signs of losing connection',
      'Needs frequent validation to feel safe in the relationship',
      'Intensity rises quickly after mixed signals or distance'
    ]
  },
  {
    styleName: 'Avoidant',
    tone: 'avoidant',
    meaning: 'Protects independence by downplaying needs and emotional vulnerability.',
    quickIdentifiers: [
      'Pulls away when conversations become emotionally deep',
      'Feels crowded by frequent check-ins or dependence',
      'Uses logic and self-reliance to avoid emotional exposure'
    ]
  },
  {
    styleName: 'Disorganized',
    tone: 'disorganized',
    meaning: 'Wants closeness and fears it at the same time, creating mixed reactions.',
    quickIdentifiers: [
      'Cycles between pursuit and sudden shutdowns',
      'Trust shifts quickly after small triggers',
      'Feels safest when near a partner, then overwhelmed by that closeness'
    ]
  }
];

const unlockableOrbs = [
  { fileNumber: 1, name: 'Ember', image: Orb1 },
  { fileNumber: 2, name: 'Anchor', image: Orb2 },
  { fileNumber: 3, name: 'Veil', image: Orb3 },
  { fileNumber: 4, name: 'Pulse', image: Orb4 },
  { fileNumber: 5, name: 'Haven', image: Orb5 },
  { fileNumber: 6, name: 'Forge', image: Orb6 },
  { fileNumber: 7, name: 'Bloom', image: Orb7 },
  { fileNumber: 8, name: 'Solace', image: Orb8 },
  { fileNumber: 9, name: 'Vow', image: Orb9 },
  { fileNumber: 10, name: 'Ascend', image: Orb10 },
  { fileNumber: 11, name: 'Ether', image: Orb11 },
  { fileNumber: 12, name: 'Radiance', image: Orb12 },
  { fileNumber: 13, name: 'Zenith', image: Orb13 },
  { fileNumber: 14, name: 'Sovereign', image: Orb14 },
  { fileNumber: 15, name: 'Transcend', image: Orb15 },
  { fileNumber: 16, name: 'Actualize', image: Orb16 }
];

const unclingMobileAppSchema = {
  '@context': 'https://schema.org',
  '@type': 'MobileApplication',
  name: 'Uncling',
  alternateName: ['Uncling App', 'Uncling No Contact Tracker', 'Uncling Breakup Recovery'],
  operatingSystem: 'iOS, Android',
  applicationCategory: 'HealthApplication',
  applicationSubCategory: 'Breakup recovery app',
  url: 'https://uncling.app/',
  downloadUrl: [appStoreUrl, googlePlayUrl],
  installUrl: [appStoreUrl, googlePlayUrl],
  sameAs: [
    appStoreUrl,
    googlePlayUrl,
    'https://uncling.app/no-contact-tracker',
    'https://uncling.app/limerence-app',
    'https://uncling.app/attachment-style-quiz'
  ],
  description:
    'Uncling is a breakup recovery app with a no contact tracker, guided journaling, daily affirmations, healing tasks, and community support for people recovering from heartbreak, limerence, and obsessive attachment.',
  featureList: [
    'No contact tracker',
    'Guided breakup journaling',
    'Personalized breakup recovery lessons',
    'Daily affirmations',
    'Daily healing tasks',
    'Breakup recovery progress tracking',
    'Limerence recovery support',
    'Attachment style quiz'
  ],
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock'
  },
  publisher: {
    '@type': 'Organization',
    name: 'Uncling',
    url: 'https://uncling.app/'
  }
};

const unclingOrganizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Uncling',
  url: 'https://uncling.app/',
  logo: 'https://uncling.app/icon-dark-white.png',
  sameAs: [appStoreUrl, googlePlayUrl],
  description:
    'Uncling creates breakup recovery tools for no contact, limerence recovery, attachment awareness, guided journaling, daily affirmations, and emotional healing after heartbreak.'
};

const unclingWebsiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Uncling',
  alternateName: ['Uncling App', 'Uncling Breakup Recovery', 'Uncling No Contact Tracker'],
  url: 'https://uncling.app/',
  description:
    'Uncling is the official website for the breakup recovery app that helps people maintain no contact, journal through heartbreak, recover from limerence, and rebuild emotional independence.',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://uncling.app/blog?s={search_term_string}',
    'query-input': 'required name=search_term_string'
  }
};

const homepageFaqs = [
  {
    question: 'What is Uncling?',
    answer:
      'Uncling is a breakup recovery app designed to help people heal from heartbreak. It features a no contact tracker, personalized journaling, community support, and science-based methods to transform pain into strength. The word uncling means to release emotional attachment and rebuild independence.'
  },
  {
    question: 'How does Uncling help with heartbreak?',
    answer:
      'Uncling offers tailored healing strategies, a no contact tracker to monitor your progress, community forums for support, and guided exercises to help you process emotions and move forward.'
  },
  {
    question: 'Is Uncling free?',
    answer:
      'Uncling offers a free trial, with premium features available through subscription to access advanced tools and personalized coaching.'
  },
  {
    question: 'Can I use Uncling on mobile?',
    answer: 'Yes, Uncling is available as a mobile app for iOS.'
  },
  {
    question: 'What if I need professional help?',
    answer:
      "Uncling is a supportive tool, not a replacement for professional help. We recommend consulting a licensed therapist if you're experiencing severe distress."
  }
];

type HomepageBlogPost = {
  id: string;
  slug: string;
  title: string;
  date: string;
  excerpt: string;
  image?: string;
  category?: string;
};

const isExQuizPath = (path: string) => /^\/ex-test\/?$/i.test(path);

const AdsRouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (isExQuizPath(location.pathname)) return;
    const path = `${location.pathname}${location.search}`;
    trackOpenAIAdsPageView(path);
    // Stash fbclid before the router strips it, then count the route as a view.
    captureFbclid();
    trackMetaPageView(path);
  }, [location.pathname, location.search]);

  return null;
};

const LAUNCH_SEEN_KEY = 'uncling:launch-seen';

// The brand intro used to run on every page load and hold the page at
// opacity:0 for 2s, which capped LCP at ~2s against Google's 2.5s "good"
// threshold. It now runs once per session, never for reduced-motion visitors,
// and never blocks the content underneath it from painting.
const shouldPlayLaunchScreen = () => {
  if (typeof window === 'undefined' || isExQuizPath(window.location.pathname)) return false;

  try {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    return window.sessionStorage.getItem(LAUNCH_SEEN_KEY) !== '1';
  } catch {
    // Private mode or blocked storage: skip the intro rather than replay it.
    return false;
  }
};

function App() {
  const [showLaunchScreen, setShowLaunchScreen] = useState(shouldPlayLaunchScreen);
  const [showQuizWelcome, setShowQuizWelcome] = useState(true);
  const [openHomepageFaqIndex, setOpenHomepageFaqIndex] = useState<number | null>(null);
  const [latestHomepagePosts, setLatestHomepagePosts] = useState<HomepageBlogPost[]>([]);
  const attachmentCarouselShellRef = useRef<HTMLDivElement | null>(null);
  const attachmentCarouselTrackRef = useRef<HTMLDivElement | null>(null);
  
  const handleLaunchComplete = React.useCallback(() => {
    try {
      window.sessionStorage.setItem(LAUNCH_SEEN_KEY, '1');
    } catch {
      // Storage unavailable; the intro simply plays again next load.
    }
    setShowLaunchScreen(false);
  }, []);
  
  const handleWaitlistClick = () => {
    trackAppStoreClick('homepage_cta');
    openAppStoreWithOpenAIAds('homepage_cta');
  };

  const handleQuizDownloadClick = () => {
    trackAppStoreClick('quiz_result');
    openAppStoreWithOpenAIAds('quiz_result');
  };

  useEffect(() => {
    fetch('/blog/posts.json')
      .then(res => res.json())
      .then((posts: HomepageBlogPost[]) => setLatestHomepagePosts(posts.slice(0, 4)))
      .catch(err => console.error('Failed to load latest homepage posts', err));
  }, []);

  useEffect(() => {
    const shell = attachmentCarouselShellRef.current;
    const track = attachmentCarouselTrackRef.current;

    if (!shell || !track) {
      return;
    }

    const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileCarouselQuery = window.matchMedia('(max-width: 768px)');

    const getBaseSpeed = () => (reduceMotionQuery.matches ? 0 : -34);
    const maxBoostPxPerSecond = 1400;
    const boostDecayPerSecond = 2.5;
  const dragStartThresholdPx = 8;

    let loopWidth = Math.max(track.scrollWidth / 2, 1);
    let offsetX = 0;
    let boostVelocity = 0;
    let lastFrameTime = performance.now();
    let rafId = 0;
    let isAnimating = false;
    let isInView = true;
  let isMobileViewport = mobileCarouselQuery.matches;
  let isPausedByTap = false;

    let isDragging = false;
    let activePointerId: number | null = null;
    let lastPointerX = 0;
    let lastPointerTime = 0;
  let pointerDownX = 0;
  let pointerDownY = 0;
  let didPointerMove = false;

    const applyTransform = () => {
      track.style.transform = `translate3d(${offsetX}px, 0, 0)`;
    };

    const wrapOffset = () => {
      while (offsetX <= -loopWidth) {
        offsetX += loopWidth;
      }

      while (offsetX > 0) {
        offsetX -= loopWidth;
      }
    };

    const recalcLoopWidth = () => {
      loopWidth = Math.max(track.scrollWidth / 2, 1);
      wrapOffset();
      applyTransform();
    };

    const tick = (now: number) => {
      if (!isAnimating) {
        return;
      }

      const deltaTimeSeconds = Math.min((now - lastFrameTime) / 1000, 0.05);
      lastFrameTime = now;
      const isAutoScrollPaused = isMobileViewport && isPausedByTap && !isDragging;

      if (!isDragging) {
        boostVelocity *= Math.exp(-boostDecayPerSecond * deltaTimeSeconds);
        if (Math.abs(boostVelocity) < 0.5) {
          boostVelocity = 0;
        }
      }

      const shouldContinue = (isInView && !isAutoScrollPaused) || isDragging || Math.abs(boostVelocity) > 0.5;

      if (shouldContinue) {
        if (!isAutoScrollPaused) {
          offsetX += (getBaseSpeed() + boostVelocity) * deltaTimeSeconds;
        }
        wrapOffset();
        applyTransform();
        rafId = window.requestAnimationFrame(tick);
        return;
      }

      isAnimating = false;
    };

    const startAnimation = () => {
      if (isAnimating) {
        return;
      }

      isAnimating = true;
      lastFrameTime = performance.now();
      rafId = window.requestAnimationFrame(tick);
    };

    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType === 'mouse' && event.button !== 0) {
        return;
      }

      activePointerId = event.pointerId;
      lastPointerX = event.clientX;
      lastPointerTime = performance.now();
      pointerDownX = event.clientX;
      pointerDownY = event.clientY;
      didPointerMove = false;

      if (!isMobileViewport) {
        isDragging = true;
        shell.classList.add('is-dragging');
      }

      shell.setPointerCapture(event.pointerId);
      startAnimation();
    };

    const onPointerMove = (event: PointerEvent) => {
      if (activePointerId !== event.pointerId) {
        return;
      }

      if (isMobileViewport && !isDragging) {
        const travelX = event.clientX - pointerDownX;
        const travelY = event.clientY - pointerDownY;
        const travelDistance = Math.hypot(travelX, travelY);

        if (travelDistance < dragStartThresholdPx) {
          return;
        }

        didPointerMove = true;
        isDragging = true;
        isPausedByTap = false;
        boostVelocity = 0;
        lastPointerX = event.clientX;
        lastPointerTime = performance.now();
        shell.classList.add('is-dragging');
      }

      if (!isDragging) {
        return;
      }

      const now = performance.now();
      const deltaX = event.clientX - lastPointerX;
      const deltaTimeMs = Math.max(now - lastPointerTime, 1);

      offsetX += deltaX;
      wrapOffset();
      applyTransform();

      const swipeVelocity = (deltaX / deltaTimeMs) * 1000;
      boostVelocity = Math.max(
        -maxBoostPxPerSecond,
        Math.min(maxBoostPxPerSecond, boostVelocity * 0.3 + swipeVelocity * 1.35)
      );

      lastPointerX = event.clientX;
      lastPointerTime = now;
    };

    const endInteraction = (pointerId: number) => {
      if (activePointerId !== pointerId) {
        return;
      }

      isDragging = false;
      activePointerId = null;
      shell.classList.remove('is-dragging');

      if (isMobileViewport) {
        boostVelocity = 0;
      }

      if (shell.hasPointerCapture(pointerId)) {
        shell.releasePointerCapture(pointerId);
      }
    };

    const onPointerUp = (event: PointerEvent) => {
      if (activePointerId !== event.pointerId) {
        return;
      }

      const isTap = isMobileViewport && !isDragging && !didPointerMove;
      if (isTap) {
        isPausedByTap = !isPausedByTap;
        boostVelocity = 0;
      }

      endInteraction(event.pointerId);

      if (!isPausedByTap) {
        startAnimation();
      }
    };

    const onPointerCancel = (event: PointerEvent) => {
      endInteraction(event.pointerId);
    };

    const onPointerLeave = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') {
        return;
      }

      endInteraction(event.pointerId);
    };

    const onMobileViewportChange = (event: MediaQueryListEvent) => {
      isMobileViewport = event.matches;

      if (!isMobileViewport) {
        isPausedByTap = false;
      }

      boostVelocity = 0;
      startAnimation();
    };

    const resizeObserver = new ResizeObserver(() => {
      recalcLoopWidth();
      startAnimation();
    });

    const visibilityObserver = new IntersectionObserver(
      (entries) => {
        isInView = entries.some((entry) => entry.isIntersecting);
        if (isInView) {
          startAnimation();
        }
      },
      { threshold: 0.05 }
    );

    shell.addEventListener('pointerdown', onPointerDown);
    shell.addEventListener('pointermove', onPointerMove);
    shell.addEventListener('pointerup', onPointerUp);
    shell.addEventListener('pointercancel', onPointerCancel);
    shell.addEventListener('pointerleave', onPointerLeave);
    mobileCarouselQuery.addEventListener('change', onMobileViewportChange);

    resizeObserver.observe(shell);
    resizeObserver.observe(track);
    visibilityObserver.observe(shell);

    recalcLoopWidth();
    applyTransform();
    startAnimation();

    return () => {
      shell.classList.remove('is-dragging');
      window.cancelAnimationFrame(rafId);

      shell.removeEventListener('pointerdown', onPointerDown);
      shell.removeEventListener('pointermove', onPointerMove);
      shell.removeEventListener('pointerup', onPointerUp);
      shell.removeEventListener('pointercancel', onPointerCancel);
      shell.removeEventListener('pointerleave', onPointerLeave);
      mobileCarouselQuery.removeEventListener('change', onMobileViewportChange);

      resizeObserver.disconnect();
      visibilityObserver.disconnect();
    };
  }, []);

  return (
    <Router>
      <AdsRouteTracker />
      {showLaunchScreen && <LaunchScreen onComplete={handleLaunchComplete} />}
      <div className="page-container">
        <Suspense fallback={null}>
        <Routes>
          <Route path="/careers" element={<Careers />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/security" element={<Security />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />
          <Route path="/faqs" element={<FAQs />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/cancel-subscription" element={<CancelSubscription />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/no-contact-tracker" element={<NoContactTracker />} />
          <Route path="/limerence-app" element={<LimerenceApp />} />
          <Route path="/ex-test" element={<ExQuiz />} />
          <Route path="/attachment-style-quiz" element={<AttachmentStyleQuiz onDownloadClick={handleQuizDownloadClick} />} />
          <Route path="/attachment-style-quiz/:styleSlug" element={<AttachmentStyleResultPage />} />
          <Route path="/" element={
            <>
              {!showLaunchScreen && showQuizWelcome && (
                <QuizWelcomeModal onDismiss={() => setShowQuizWelcome(false)} />
              )}
              <Helmet>
                <title>Uncling | Breakup Recovery & No Contact Tracker App</title>
                <meta name="description" content="Uncling is a breakup recovery app for no contact tracking, guided journaling, daily affirmations, limerence recovery, and emotional healing after heartbreak." />
                <meta name="keywords" content="no contact tracker, no contact app, breakup recovery app, limerence app, obsessive love recovery, anxious attachment, breakup support community, heartbreak healing tracker, no contact rule" />
                <link rel="canonical" href="https://uncling.app/" />
                <link rel="preload" as="image" href={ThreeMockups} fetchPriority="high" />
                <meta property="og:title" content="Uncling | Breakup Recovery & No Contact Tracker App" />
                <meta property="og:description" content="Uncling helps you maintain no contact, journal through heartbreak, recover from limerence, and rebuild emotional independence." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://uncling.app/" />
                <meta property="og:image" content="https://uncling.app/icon-dark-white.png" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Uncling | Breakup Recovery & No Contact Tracker App" />
                <meta name="twitter:description" content="Uncling helps you maintain no contact, journal through heartbreak, recover from limerence, and rebuild emotional independence." />
                <meta name="twitter:image" content="https://uncling.app/icon-dark-white.png" />
                
                <script type="application/ld+json">
                  {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                      {
                        "@type": "ListItem",
                        "position": 1,
                        "name": "Home",
                        "item": "https://uncling.app"
                      },
                      {
                        "@type": "ListItem",
                        "position": 2,
                        "name": "No Contact Tracker",
                        "item": "https://uncling.app/no-contact-tracker"
                      },
                      {
                        "@type": "ListItem",
                        "position": 3,
                        "name": "Limerence App",
                        "item": "https://uncling.app/limerence-app"
                      },
                      {
                        "@type": "ListItem",
                        "position": 4,
                        "name": "Blog",
                        "item": "https://uncling.app/blog"
                      }
                    ]
                  })}
                </script>
                
                <script type="application/ld+json">
                  {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Product",
                    "name": "Uncling",
                    "alternateName": "Uncling - No Contact Tracker & Breakup Recovery App",
                    "description": "Free breakup recovery app with no contact tracker, guided journals, and community support",
                    "brand": {
                      "@type": "Brand",
                      "name": "Uncling"
                    },
                    "offers": {
                      "@type": "Offer",
                      "price": "0",
                      "priceCurrency": "USD"
                    },
                    "aggregateRating": {
                      "@type": "AggregateRating",
                      "ratingValue": "4.9",
                      "reviewCount": "34",
                      "bestRating": "5",
                      "worstRating": "1"
                    },
                    "review": [
                      {
                        "@type": "Review",
                        "author": {
                          "@type": "Person",
                          "name": "Sarah M."
                        },
                        "reviewRating": {
                          "@type": "Rating",
                          "ratingValue": "5",
                          "bestRating": "5"
                        },
                        "reviewBody": "Love the start and what I’ve done already brings me peace"
                      },
                      {
                        "@type": "Review",
                        "author": {
                          "@type": "Person",
                          "name": "Michael R."
                        },
                        "reviewRating": {
                          "@type": "Rating",
                          "ratingValue": "5",
                          "bestRating": "5"
                        },
                        "reviewBody": "Great app i love it so much"
                      },
                      {
                        "@type": "Review",
                        "author": {
                          "@type": "Person",
                          "name": "Jennifer L."
                        },
                        "reviewRating": {
                          "@type": "Rating",
                          "ratingValue": "5",
                          "bestRating": "5"
                        },
                        "reviewBody": "What a beautiful app for a bad moment in my life, the app is STUNNING"
                      }
                    ]
                  })}
                </script>

                <script type="application/ld+json">
                  {JSON.stringify(unclingMobileAppSchema)}
                </script>

                <script type="application/ld+json">
                  {JSON.stringify(unclingOrganizationSchema)}
                </script>

                <script type="application/ld+json">
                  {JSON.stringify(unclingWebsiteSchema)}
                </script>
              </Helmet>
              <div className="gradient-section">
                <ScrollSectionBackground edge="top" />
                <Navbar />
                
                <main className="hero-content">
                  <div className="hero-text">
                    <div className="hero-rating-pill" aria-label="4.9 star rating on App Store">
                      <Star className="hero-rating-icon" aria-hidden="true" />
                      <span>4.9 star rating on App Store</span>
                    </div>
                    <h1 className="hero-title">
                      <span className="title-line-1">Stay in no contact.</span>
                      <span className="title-line-2">Take care of yourself.</span>
                    </h1>
                    <p className="hero-subtitle">
                      <span className="subtitle-desktop">Release emotional attachment with no contact tracking, guided journaling, daily affirmations, and support built for heartbreak recovery.</span>
                      <span className="subtitle-mobile">Master no contact & heal from heartbreak.</span>
                    </p>
                    <div className="store-buttons-container">
                      <AppStoreQrHover placement="bottom">
                        <a
                          href={appStoreUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="app-store-link"
                          aria-label="Download Uncling on the Apple App Store"
                          onClick={() => {
                            trackAppStoreClick('homepage_hero');
                            trackOpenAIAdsAppDownloadClick('app_store', 'homepage_hero');
                          }}
                        >
                          <img src={AppStore} alt="Download Uncling breakup recovery app on the Apple App Store" className="app-store-button" />
                        </a>
                      </AppStoreQrHover>
                      <a
                        href={googlePlayUrl}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="app-store-link"
                        aria-label="Get Uncling on Google Play Store"
                        onClick={() => trackOpenAIAdsAppDownloadClick('google_play', 'homepage_hero')}
                      >
                        <img src={GooglePlay} alt="Get Uncling no contact tracker app on Google Play Store" className="google-play-button" />
                      </a>
                    </div>
                    <nav className="hero-quiz-links" aria-label="Free relationship quizzes">
                      <Link to="/ex-test">
                        How over your ex are you? <span aria-hidden="true">→</span>
                        <small>2 min</small>
                      </Link>
                      <Link to="/attachment-style-quiz">
                        What’s your attachment style? <span aria-hidden="true">→</span>
                        <small>5 min</small>
                      </Link>
                    </nav>
                  </div>
                  <img
                    src={ThreeMockups}
                    alt="Uncling app interface showing no contact tracker, journal entries, and healing progress dashboard on mobile devices"
                    className="hero-mockups"
                    loading="eager"
                    decoding="async"
                    onError={(e) => {
                      console.error('Failed to load mockup image');
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </main>
              </div>

              <section className="statistics-banner" aria-label="No contact statistics">
                <div className="statistics-content">
                  <ScrollRevealText />
                </div>
              </section>

              <section className="attachment-carousel-section" aria-label="Attachment styles overview">
                <div className="attachment-carousel-shell" ref={attachmentCarouselShellRef}>
                  <div
                    className="attachment-carousel-track"
                    ref={attachmentCarouselTrackRef}
                    role="list"
                    aria-live="off"
                  >
                    {[...attachmentStyleCards, ...attachmentStyleCards].map((card, index) => {
                      const isDuplicate = index >= attachmentStyleCards.length;

                      return (
                        <article
                          key={`${card.styleName}-${index}`}
                          className={`attachment-style-card attachment-style-card-${card.tone}`}
                          role="listitem"
                          aria-hidden={isDuplicate}
                          tabIndex={isDuplicate ? -1 : 0}
                        >
                          <p className="attachment-card-kicker">Attachment style</p>
                          <h3 className="attachment-card-title">{card.styleName}</h3>
                          <p className="attachment-card-meaning">{card.meaning}</p>
                          <p className="attachment-card-subtitle">Quick ways to identify it</p>
                          <ul className="attachment-card-identifiers">
                            {card.quickIdentifiers.map((identifier) => (
                              <li key={identifier}>{identifier}</li>
                            ))}
                          </ul>
                        </article>
                      );
                    })}
                  </div>
                </div>
              </section>

              <section className="black-section" aria-label="Recovery process visualization">
                <AnimatedRectangle className="info-rectangle" delay={0}>
                  <img src={aNewHeart} alt="Illustration showing emotional healing journey from broken heart to renewed hope and self-discovery" className="rectangle-heart" loading="lazy" decoding="async" />
                </AnimatedRectangle>
                <AnimatedRectangle className="info-rectangle-second" delay={100}>
                  <img src={RecoverGraph} alt="Graph showing typical breakup recovery timeline with emotional ups and downs leading to healing" className="rectangle-graph" loading="lazy" decoding="async" />
                </AnimatedRectangle>
                <AnimatedRectangle className="info-rectangle-third" delay={200}>
                  <img src={UnclingEffectiveness} alt="Comparison chart showing Uncling users heal 3x faster than those without structured support" className="rectangle-effectiveness" loading="lazy" decoding="async" />
                </AnimatedRectangle>
                <AnimatedRectangle className="info-rectangle-fourth" delay={300}>
                  <img src={YouCanHeal} alt="Empowering message that anyone can heal from heartbreak with the right tools and support" className="rectangle-heal" loading="lazy" decoding="async" />
                </AnimatedRectangle>
              </section>

              <section className="features-showcase-section" id="features" aria-label="App features">
                <div className="features-showcase-container">
                  <div className="how-it-works-badge">How It Works</div>
                  <h2 className="features-showcase-title">Powerful Tools for Your Healing Journey</h2>
                  
                  <AnimatedFeatureItem index={0}>
                    <div className="feature-content">
                      <h3>No Contact Tracker</h3>
                      <p className="feature-description-desktop">Stay accountable with our no contact tracker featuring home screen and lock screen widgets. Visualize your streak and celebrate milestones as you build emotional freedom one day at a time.</p>
                      <p className="feature-description-mobile">Track your no contact streak with widgets. Visualize progress and celebrate milestones.</p>
                    </div>
                    <div className="feature-image">
                      <img src={NoContactTrackerImage} alt="No contact tracker app interface showing day counter, milestone celebrations, and home screen widget for staying accountable during breakup recovery" loading="lazy" decoding="async" />
                    </div>
                  </AnimatedFeatureItem>

                  <AnimatedFeatureItem index={1}>
                    <div className="feature-content">
                      <h3>Guided Journals</h3>
                      <p className="feature-description-desktop">Process your emotions through structured journaling prompts. Reflect on your progress, explore your feelings, and document your transformation with therapeutic writing exercises.</p>
                      <p className="feature-description-mobile">Process emotions with guided prompts. Reflect and document your healing journey.</p>
                    </div>
                    <div className="feature-image">
                      <img src={JournalsImage} alt="Guided journaling interface with therapeutic prompts for processing breakup emotions and tracking healing progress" loading="lazy" decoding="async" />
                    </div>
                  </AnimatedFeatureItem>

                  <AnimatedFeatureItem index={2}>
                    <div className="feature-content">
                      <h3>Personalized Lessons</h3>
                      <p className="feature-description-desktop">Learn the emotional patterns behind attachment, cravings, and setbacks with bite-sized lessons built for breakup recovery. Turn confusing moments into practical insight you can use every day.</p>
                      <p className="feature-description-mobile">Learn breakup recovery skills with short lessons that turn hard moments into practical insight.</p>
                    </div>
                    <div className="feature-image">
                      <img src={LessonsImage} alt="Personalized lessons interface with breakup recovery education for understanding attachment patterns and healing skills" loading="lazy" decoding="async" />
                    </div>
                  </AnimatedFeatureItem>

                  <AnimatedFeatureItem index={3}>
                    <div className="feature-content">
                      <h3>Community</h3>
                      <p className="feature-description-desktop">Connect with others navigating heartbreak and no contact. Share your experiences, exchange advice, and find encouragement as you take the next step in your healing.</p>
                      <p className="feature-description-mobile">Share experiences, exchange advice, and find encouragement from others navigating heartbreak.</p>
                    </div>
                    <div className="feature-image">
                      <img src={CommunityImage} alt="Uncling community interface showing advice posts, conversations, and member support" loading="lazy" decoding="async" />
                    </div>
                  </AnimatedFeatureItem>

                  <AnimatedFeatureItem index={4}>
                    <div className="feature-content">
                      <h3>Collect Hearts</h3>
                      <p className="feature-description-desktop">Visualize your recovery progress with our unique heart unlock system. Track your healing milestones and celebrate each step forward as you unlock new hearts on your journey to emotional freedom.</p>
                      <p className="feature-description-mobile">Unlock hearts as you heal. Track milestones and celebrate your progress.</p>
                    </div>
                    <div className="feature-image">
                      <img src={HeartsImage} alt="Heart collection system showing unlocked hearts representing healing milestones and recovery progress" loading="lazy" decoding="async" />
                    </div>
                  </AnimatedFeatureItem>

                  <AnimatedFeatureItem index={5}>
                    <div className="feature-content">
                      <h3>Screen Time Shield</h3>
                      <p className="feature-description-desktop">Block selected apps and categories with Screen Time Shield. Give yourself space to ride out urges and stay focused on your healing.</p>
                      <p className="feature-description-mobile">Block distracting apps and categories. Give yourself space to ride out an urge.</p>
                    </div>
                    <div className="feature-image">
                      <img src={ScreenTimeImage} alt="Screen time dashboard showing a daily score with app use, pickups, and quiet time indicators" loading="lazy" decoding="async" />
                    </div>
                  </AnimatedFeatureItem>

                  <AnimatedFeatureItem index={6}>
                    <div className="feature-content">
                      <h3>Daily Affirmations</h3>
                      <p className="feature-description-desktop">Start each day with powerful, positive affirmations designed to rebuild your self-worth and confidence. Transform negative thought patterns into empowering beliefs.</p>
                      <p className="feature-description-mobile">Build self-worth with daily affirmations. Transform negative thoughts into empowering beliefs.</p>
                    </div>
                    <div className="feature-image">
                      <img src={AffirmationsImage} alt="Daily affirmation cards with empowering messages for rebuilding self-worth and confidence after heartbreak" loading="lazy" decoding="async" />
                    </div>
                  </AnimatedFeatureItem>

                  <AnimatedFeatureItem index={7}>
                    <div className="feature-content">
                      <h3>Daily Tasks</h3>
                      <p className="feature-description-desktop">Build healthy habits and stay consistent with personalized daily tasks. Add tasks to your home screen widget and track your progress with streaks that keep you motivated on your healing journey.</p>
                      <p className="feature-description-mobile">Build healthy habits with daily tasks. Track streaks and stay motivated.</p>
                    </div>
                    <div className="feature-image">
                      <img src={DailyTasksImage} alt="Daily task tracker with habit streaks and home screen widget for building consistent healing routines" loading="lazy" decoding="async" />
                    </div>
                  </AnimatedFeatureItem>
                </div>
              </section>

              <div className="download-cta-section">
                <AppStoreQrHover placement="top">
                  <button className="big-download-button" onClick={handleWaitlistClick}>
                    Download
                  </button>
                </AppStoreQrHover>
              </div>
              
              <section className="testimonials-section" aria-label="User testimonials and success stories">
                <div className="testimonials-container">
                  <div className="real-reviews-badge">Success Stories</div>
                  <h2 className="testimonials-title">Real people. Real healing.</h2>
                  
                  <div className="testimonials-grid">
                    <div className="testimonial-card" itemScope itemType="https://schema.org/Review">
                      <p className="testimonial-text" itemProp="reviewBody">"Love the start and what I’ve done already brings me peace"</p>
                      <div className="testimonial-author" itemProp="author" itemScope itemType="https://schema.org/Person">
                        <img src={SarahMImage} alt="Sarah M., verified Uncling member" className="author-avatar" loading="lazy" decoding="async" />
                        <div className="author-info">
                          <div className="author-header">
                            <span className="author-name" itemProp="name">Sarah M.</span>
                            <div className="stars" itemProp="reviewRating" itemScope itemType="https://schema.org/Rating">
                              <meta itemProp="ratingValue" content="5" />
                              <meta itemProp="bestRating" content="5" />
                              ★★★★★
                            </div>
                          </div>
                          <span className="author-title">Verified Member</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="testimonial-card" itemScope itemType="https://schema.org/Review">
                      <p className="testimonial-text" itemProp="reviewBody">"Great app i love it so much"</p>
                      <div className="testimonial-author" itemProp="author" itemScope itemType="https://schema.org/Person">
                        <img src={MichaelRImage} alt="Michael R., verified Uncling member" className="author-avatar" loading="lazy" decoding="async" />
                        <div className="author-info">
                          <div className="author-header">
                            <span className="author-name" itemProp="name">Michael R.</span>
                            <div className="stars" itemProp="reviewRating" itemScope itemType="https://schema.org/Rating">
                              <meta itemProp="ratingValue" content="5" />
                              <meta itemProp="bestRating" content="5" />
                              ★★★★★
                            </div>
                          </div>
                          <span className="author-title">Verified Member</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="testimonial-card" itemScope itemType="https://schema.org/Review">
                      <p className="testimonial-text" itemProp="reviewBody">"What a beautiful app for a bad moment in my life, the app is STUNNING"</p>
                      <div className="testimonial-author" itemProp="author" itemScope itemType="https://schema.org/Person">
                        <img src={JenniferLImage} alt="Jennifer L., verified Uncling member" className="author-avatar" loading="lazy" decoding="async" />
                        <div className="author-info">
                          <div className="author-header">
                            <span className="author-name" itemProp="name">Jennifer L.</span>
                            <div className="stars" itemProp="reviewRating" itemScope itemType="https://schema.org/Rating">
                              <meta itemProp="ratingValue" content="5" />
                              <meta itemProp="bestRating" content="5" />
                              ★★★★★
                            </div>
                          </div>
                          <span className="author-title">Verified Member</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section className="orb-unlocks-section" aria-labelledby="orb-unlocks-title">
                <div className="orb-unlocks-header">
                  <h2 id="orb-unlocks-title" className="orb-unlocks-title">Orbs To Visualize Your Progress</h2>
                  <p className="orb-unlocks-description">achievements</p>
                </div>

                <div className="orb-carousel" aria-label="Unlockable achievement orbs">
                  <div className="orb-carousel-track">
                    {[...unlockableOrbs, ...unlockableOrbs].map((orb, index) => (
                      <div className="orb-card" key={`unlockable-orb-${orb.fileNumber}-${index}`} aria-hidden={index >= unlockableOrbs.length}>
                        <img
                          src={orb.image}
                          alt={`${orb.name} unlockable orb achievement`}
                          className="orb-image" loading="lazy" decoding="async"
                        />
                        <span className="orb-name">{orb.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
              
              <div className="home-definition-section">
                <div className="uncling-definition-container">
                  <div className="definition-card">
                    <div className="definition-header">
                      <h2 className="definition-word">uncling</h2>
                    </div>
                    <div className="definition-pronunciation">
                      <span className="pronunciation-bracket">[</span>uhn-<span className="pronunciation-bold">kling</span><span className="pronunciation-bracket">]</span>
                      <span className="pronunciation-slash">/ ʌnˈklɪŋ /</span>
                    </div>
                    
                    <div className="definition-part-of-speech">VERB</div>
                    
                    <div className="definition-list">
                      <div className="definition-item">
                        <span className="definition-number">1</span>
                        <div className="definition-text-container">
                          <p className="definition-text">
                            <span className="definition-note">(obsolete)</span> to cease from clinging or adhering.
                          </p>
                          <p className="definition-synonyms">
                            <strong>Synonyms:</strong> detach, release, let go
                          </p>
                        </div>
                      </div>
                      <div className="definition-item">
                        <span className="definition-number">2</span>
                        <div className="definition-text-container">
                          <p className="definition-text">
                            to free oneself from emotional attachment or obsession, especially after a breakup.
                          </p>
                          <p className="definition-synonyms">
                            <strong>Synonyms:</strong> heal, move on, recover
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {latestHomepagePosts.length > 0 && (
                <section className="homepage-blog-preview-section" aria-labelledby="homepage-blog-preview-title">
                  <div className="homepage-blog-preview-container">
                    <div className="homepage-blog-preview-header">
                      <h2 id="homepage-blog-preview-title" className="homepage-blog-preview-title">Latest Breakup Recovery Guides</h2>
                      <p className="homepage-blog-preview-subtitle">Fresh advice for no contact, healing, and rebuilding your life after heartbreak.</p>
                    </div>

                    <div className="homepage-blog-preview-grid">
                      {latestHomepagePosts.map((post) => (
                        <Link key={post.id} to={`/blog/${post.slug}`} className="homepage-blog-card">
                          {post.image && (
                            <div className="homepage-blog-card-image">
                              <img src={post.image} alt={post.title} loading="lazy" />
                            </div>
                          )}

                          <div className="homepage-blog-card-content">
                            <div className="homepage-blog-card-meta">
                              {post.category && <span>{post.category}</span>}
                              <time dateTime={post.date}>
                                {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}
                              </time>
                            </div>
                            <h3>{post.title}</h3>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </section>
              )}

              <section className="homepage-faq-section" aria-labelledby="homepage-faq-title">
                <div className="homepage-faq-container">
                  <p className="homepage-faq-kicker">FAQs</p>
                  <h2 id="homepage-faq-title" className="homepage-faq-title">Questions before you start healing?</h2>

                  <div className="homepage-faq-list">
                    {homepageFaqs.map((faq, index) => {
                      const isExpanded = openHomepageFaqIndex === index;
                      const answerId = `homepage-faq-answer-${index}`;

                      return (
                        <article className={`homepage-faq-item ${isExpanded ? 'is-expanded' : ''}`} key={faq.question}>
                          <h3>
                            <button
                              type="button"
                              className="homepage-faq-question"
                              aria-expanded={isExpanded}
                              aria-controls={answerId}
                              onClick={() => setOpenHomepageFaqIndex(isExpanded ? null : index)}
                            >
                              <span>{faq.question}</span>
                              <span className="homepage-faq-toggle" aria-hidden="true">
                                {isExpanded ? '-' : '+'}
                              </span>
                            </button>
                          </h3>
                          <div id={answerId} className="homepage-faq-answer" hidden={!isExpanded}>
                            <p>{faq.answer}</p>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
              </section>
              
              <Footer />
            </>
          } />
        </Routes>
        </Suspense>
      </div>
      <Analytics />
    </Router>
  );
}

export default App;
